/**
 * Versioned Data Migration
 *
 * Migrates versioned data from an older schema version to the current version
 * using sequential migration steps (v1 -> v2 -> v3 -> ...). Ported
 * near-verbatim from `ersatzteil-scraper` `src/lib/versioned/migrate.ts`
 * (relative imports only — ADR-0005), trimmed to the three modes this template
 * uses (`'throw'`, `'reset-to-default'`, `'migrate'`).
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 */

import type { IJsonSchemaPlugin, MigrationResult, Versioned } from './types';

/**
 * Migrates versioned data to the plugin's current version.
 *
 * Skip-mode contract (when `onMissingMigration === 'migrate'`): on a missing
 * step `v -> v+1`, advance the counter by one and continue. If the next
 * version's schema is registered, re-parse the in-progress value against it as
 * a defense-in-depth check.
 */
export function migrateVersionedData<T>(
  plugin: IJsonSchemaPlugin<string, T>,
  data: Versioned<unknown>
): MigrationResult<T> {
  const fromVersion = data.version;
  const targetVersion = plugin.currentVersion;

  // Future version — hard error (prevents silent data corruption).
  if (fromVersion > targetVersion) {
    throw new Error(
      `${plugin.name}: data version ${fromVersion} > current ${targetVersion}. ` +
        `Update the application before reading this file.`
    );
  }

  // Already at current version — no migration needed. (This short-circuit is
  // why the model-finder state plugin must register the new {selectedId} shape
  // as v2, not v1 — see SEC-6-1 / spec §3.8.)
  if (fromVersion === targetVersion) {
    return {
      data: { version: fromVersion, value: data.value as T },
      migrated: false,
      fromVersion,
      toVersion: fromVersion,
    };
  }

  // Sequential migration: v1 -> v2 -> ... -> current.
  let currentValue: unknown = data.value;
  let currentVersion = fromVersion;

  while (currentVersion < targetVersion) {
    const migration = plugin.migrations.get(currentVersion);

    if (!migration) {
      const policyResult = applyMissingMigrationPolicy(plugin, currentVersion);
      if (policyResult.kind === 'reset') {
        return resetToDefault(plugin, fromVersion, targetVersion);
      }
      // 'migrate' (skip-mode): advance, optionally revalidate, continue.
      currentVersion++;
      const nextSchema = plugin.schemas.get(currentVersion);
      if (nextSchema) currentValue = nextSchema.parse(currentValue);
      continue;
    }

    try {
      currentValue = migration(currentValue);
      currentVersion++;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // `Error(msg, { cause })` is ES2022; this template targets ES2019, so set
      // the cause manually for parity without the two-arg constructor.
      const wrapped = new Error(
        `Migration failed for "${plugin.name}" v${currentVersion} -> v${currentVersion + 1}: ${message}`
      );
      (wrapped as Error & { cause?: unknown }).cause = error instanceof Error ? error : undefined;
      if (plugin.onMissingMigration === 'reset-to-default') {
        return resetToDefault(plugin, fromVersion, targetVersion);
      }
      throw wrapped;
    }
  }

  return {
    data: { version: currentVersion, value: currentValue as T },
    migrated: currentVersion > fromVersion,
    fromVersion,
    toVersion: currentVersion,
  };
}

type PolicyResult = { kind: 'reset' } | { kind: 'skip' };

/**
 * Apply the plugin's `onMissingMigration` policy when a step is absent at
 * `v -> v+1`. Returns a discriminated result so the caller builds the envelope.
 *
 * @throws Error when `onMissingMigration === 'throw'`.
 */
function applyMissingMigrationPolicy<TName extends string, TLatest>(
  plugin: IJsonSchemaPlugin<TName, TLatest>,
  fromVersion: number
): PolicyResult {
  if (plugin.onMissingMigration === 'throw') {
    throw new Error(
      `Versioned[${plugin.name}]: missing migration v${fromVersion} -> v${fromVersion + 1} ` +
        `(set onMissingMigration to 'migrate' or 'reset-to-default' to relax).`
    );
  }
  if (plugin.onMissingMigration === 'reset-to-default') {
    return { kind: 'reset' };
  }
  return { kind: 'skip' };
}

/** Returns a reset-to-default result. Throws if no `defaultValue` is configured. */
function resetToDefault<T>(
  plugin: IJsonSchemaPlugin<string, T>,
  fromVersion: number,
  targetVersion: number
): MigrationResult<T> {
  if (plugin.defaultValue === undefined) {
    throw new Error(
      `Versioned[${plugin.name}]: cannot reset to default: no defaultValue configured. ` +
        `Plugins with onMissingMigration='reset-to-default' must provide a defaultValue.`
    );
  }
  return {
    data: { version: targetVersion, value: plugin.defaultValue },
    migrated: true,
    fromVersion,
    toVersion: targetVersion,
  };
}
